import { useState } from 'react';
import WatchlistReportService, { ReportAlumniRow } from './WatchlistReportService';
import { STAGES, STAGE_LABELS } from './labelMaps';

const Overlay = ({ title, onClose, children, footer }: { title: string; onClose: () => void; children: React.ReactNode; footer: React.ReactNode }) => (
    <div className="modal d-block" tabIndex={-1} style={{ background: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow-lg rounded-4">
                <div className="modal-header px-4 py-3 border-bottom d-flex justify-content-between align-items-center">
                    <h5 className="modal-title font-16 font-600 text-dark-blue">{title}</h5>
                    <button type="button" className="btn-close shadow-none" onClick={onClose}></button>
                </div>
                <div className="modal-body p-4">{children}</div>
                <div className="modal-footer px-4 py-3 border-top d-flex justify-content-end gap-2">{footer}</div>
            </div>
        </div>
    </div>
);

const useSave = (fn: () => Promise<any>, onDone: () => void) => {
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const run = async () => {
        setSaving(true);
        setError(null);
        try {
            await fn();
            onDone();
        } catch (e: any) {
            setError(e?.response?.data?.error?.message || e?.message || 'Action failed.');
            setSaving(false);
        }
    };
    return { saving, error, run };
};

export const MarkPitchReadyModal = ({ row, onClose, onDone }: { row: ReportAlumniRow; onClose: () => void; onDone: () => void }) => {
    const [phase, setPhase] = useState('1');
    const [notes, setNotes] = useState('');
    const { saving, error, run } = useSave(
        () => WatchlistReportService.markPitchReady({
            institute_id: row.institute_id as number, student_id: row.student_id, watchlist_code: row.watchlist_code,
            outcome: row.outcome, reachout_phase: Number(phase), notes,
        }),
        onDone
    );
    return (
        <Overlay title={`Mark Ready for Pitch — ${row.name ?? ''}`} onClose={onClose}
            footer={<>
                <button className="btn btn-outline-secondary px-4 rounded-3" onClick={onClose} disabled={saving}>Cancel</button>
                <button className="btn btn-primary px-4 rounded-3" onClick={run} disabled={saving}>{saving ? 'Saving…' : 'Mark Ready'}</button>
            </>}>
            {error && <div className="alert alert-danger font-12">{error}</div>}
            <div className="mb-3">
                <label className="form-label text-muted font-12 font-600">Reachout Phase</label>
                <select className="form-select rounded-3 font-14" value={phase} onChange={(e) => setPhase(e.target.value)}>
                    <option value="1">Phase 1 — Immediate (highest priority)</option>
                    <option value="2">Phase 2 — This month</option>
                    <option value="3">Phase 3 — This quarter</option>
                </select>
            </div>
            <div>
                <label className="form-label text-muted font-12 font-600">Notes</label>
                <textarea className="form-control rounded-3 h-auto font-14" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional context…" />
            </div>
        </Overlay>
    );
};

export const ChangePhaseModal = ({ row, onClose, onDone }: { row: ReportAlumniRow; onClose: () => void; onDone: () => void }) => {
    const [phase, setPhase] = useState(String(row.reachout_phase ?? '1'));
    const { saving, error, run } = useSave(
        () => WatchlistReportService.updatePhase({
            institute_id: row.institute_id as number, student_id: row.student_id, watchlist_code: row.watchlist_code, reachout_phase: Number(phase),
        }),
        onDone
    );
    return (
        <Overlay title={`Change Pitch Phase — ${row.name ?? ''}`} onClose={onClose}
            footer={<>
                <button className="btn btn-outline-secondary px-4 rounded-3" onClick={onClose} disabled={saving}>Cancel</button>
                <button className="btn btn-primary px-4 rounded-3" onClick={run} disabled={saving}>{saving ? 'Saving…' : 'Update Phase'}</button>
            </>}>
            {error && <div className="alert alert-danger font-12">{error}</div>}
            <label className="form-label text-muted font-12 font-600">Reachout Phase</label>
            <select className="form-select rounded-3 font-14" value={phase} onChange={(e) => setPhase(e.target.value)}>
                <option value="1">Phase 1 — Immediate (highest priority)</option>
                <option value="2">Phase 2 — This month</option>
                <option value="3">Phase 3 — This quarter</option>
            </select>
        </Overlay>
    );
};

export const MarkDormantModal = ({ row, onClose, onDone }: { row: ReportAlumniRow; onClose: () => void; onDone: () => void }) => {
    const [reason, setReason] = useState('');
    const { saving, error, run } = useSave(
        () => WatchlistReportService.markDormant({ institute_id: row.institute_id as number, student_id: row.student_id, layer: row.layer, outcome: row.outcome, reason }),
        onDone
    );
    return (
        <Overlay title={`Mark Dormant — ${row.name ?? ''}`} onClose={onClose}
            footer={<>
                <button className="btn btn-outline-secondary px-4 rounded-3" onClick={onClose} disabled={saving}>Cancel</button>
                <button className="btn btn-warning px-4 rounded-3" onClick={run} disabled={saving}>{saving ? 'Saving…' : 'Mark Dormant'}</button>
            </>}>
            {error && <div className="alert alert-danger font-12">{error}</div>}
            <p className="font-12 text-secondary">This manually overrides the alumni's state. It survives daily snapshots until cleared.</p>
            <label className="form-label text-muted font-12 font-600">Reason</label>
            <textarea className="form-control rounded-3 h-auto font-14" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is this alumni being marked dormant?" />
        </Overlay>
    );
};

export const AdvanceStageModal = ({ row, onClose, onDone, denied }: { row: ReportAlumniRow; onClose: () => void; onDone: () => void; denied?: boolean }) => {
    const [stage, setStage] = useState(denied ? 'denied' : 'engaged');
    const [reason, setReason] = useState('');
    const { saving, error, run } = useSave(
        () => WatchlistReportService.advanceStage({ institute_id: row.institute_id as number, student_id: row.student_id, outcome: row.outcome as string, target_stage: stage, reason }),
        onDone
    );
    return (
        <Overlay title={`${denied ? 'Mark Denied' : 'Advance Stage'} — ${row.name ?? ''}`} onClose={onClose}
            footer={<>
                <button className="btn btn-outline-secondary px-4 rounded-3" onClick={onClose} disabled={saving}>Cancel</button>
                <button className="btn btn-primary px-4 rounded-3" onClick={run} disabled={saving}>{saving ? 'Saving…' : 'Apply'}</button>
            </>}>
            {error && <div className="alert alert-danger font-12">{error}</div>}
            <div className="mb-3">
                <label className="form-label text-muted font-12 font-600">Target Stage</label>
                {denied ? (
                    // Mark Denied is a fixed action — the stage is always "Denied", not selectable.
                    <input className="form-control rounded-3 font-14 bg-light" value={STAGE_LABELS['denied']} readOnly disabled />
                ) : (
                    <select className="form-select rounded-3 font-14" value={stage} onChange={(e) => setStage(e.target.value)}>
                        {STAGES.map((s) => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
                    </select>
                )}
            </div>
            <label className="form-label text-muted font-12 font-600">Reason</label>
            <textarea className="form-control rounded-3 h-auto font-14" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Optional" />
        </Overlay>
    );
};

export const BulkMarkReadyModal = ({ rows, onClose, onDone }: { rows: ReportAlumniRow[]; onClose: () => void; onDone: () => void }) => {
    const [phase, setPhase] = useState('1');
    const [notes, setNotes] = useState('');
    // Bulk uses the watchlist_code/outcome of the first selected row (spec: one watchlist per bulk op).
    const first = rows[0];
    const { saving, error, run } = useSave(
        () => WatchlistReportService.bulkMarkReady({
            institute_id: first.institute_id as number, student_ids: rows.map((r) => r.student_id),
            watchlist_code: first.watchlist_code, outcome: first.outcome, reachout_phase: Number(phase), notes,
        }),
        onDone
    );
    const mixed = rows.some((r) => r.watchlist_code !== first.watchlist_code);
    return (
        <Overlay title={`Bulk Mark Ready — ${rows.length} alumni`} onClose={onClose}
            footer={<>
                <button className="btn btn-outline-secondary px-4 rounded-3" onClick={onClose} disabled={saving}>Cancel</button>
                <button className="btn btn-primary px-4 rounded-3" onClick={run} disabled={saving}>{saving ? 'Saving…' : `Mark ${rows.length} Ready`}</button>
            </>}>
            {error && <div className="alert alert-danger font-12">{error}</div>}
            {mixed && <div className="alert alert-warning font-12">Selected rows span multiple watchlists — all will be marked under <b>{first.watchlist_label}</b>.</div>}
            <div className="mb-3">
                <label className="form-label text-muted font-12 font-600">Reachout Phase</label>
                <select className="form-select rounded-3 font-14" value={phase} onChange={(e) => setPhase(e.target.value)}>
                    <option value="1">Phase 1 — Immediate</option>
                    <option value="2">Phase 2 — This month</option>
                    <option value="3">Phase 3 — This quarter</option>
                </select>
            </div>
            <label className="form-label text-muted font-12 font-600">Notes</label>
            <textarea className="form-control rounded-3 h-auto font-14" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" />
        </Overlay>
    );
};
